<script lang="ts">
  import ModalShell from './ModalShell.svelte';
  import {
    canShowNativePrompt,
    showNativePrompt,
    detectPlatform,
  } from './pwa';

  let { onClose }: { onClose: () => void } = $props();

  const platform = detectPlatform();
  const hasNativePrompt = canShowNativePrompt();

  let installing: boolean = $state(false);
  let outcomeMessage: string | null = $state(null);

  async function onInstall() {
    if (installing) return;
    installing = true;
    outcomeMessage = null;
    try {
      const result = await showNativePrompt();
      if (result === 'accepted') {
        onClose();
      } else if (result === 'dismissed') {
        outcomeMessage = "No problem — you can install later from this menu.";
      } else {
        outcomeMessage = "Couldn't open the install prompt. Try your browser's menu.";
      }
    } finally {
      installing = false;
    }
  }
</script>

<ModalShell title="Install Lazy Traveler" {onClose} role="dialog" maxWidth={460}>
  <div class="content">
    <p class="lede">
      Install on your device so it opens like a real app — full-screen, on your home screen,
      and the shell loads instantly even on a flaky connection.
    </p>

    {#if hasNativePrompt}
      <button class="btn btn-primary install-cta" type="button" onclick={onInstall} disabled={installing}>
        {installing ? 'Installing…' : 'Install'}
      </button>
      {#if outcomeMessage}
        <p class="hint">{outcomeMessage}</p>
      {/if}
    {:else if platform === 'ios'}
      <ol class="steps">
        <li>Tap the <strong>Share</strong> button at the bottom of Safari (the square with the arrow).</li>
        <li>Scroll down and choose <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong> in the top-right.</li>
      </ol>
      <p class="hint">If you're using Chrome or Firefox on iOS, switch to Safari first — only Safari supports install on iOS.</p>
    {:else if platform === 'macos-safari'}
      <ol class="steps">
        <li>Click the <strong>Share</strong> button in the Safari toolbar.</li>
        <li>Choose <strong>Add to Dock</strong>.</li>
      </ol>
      <p class="hint">Requires Safari 17 or newer.</p>
    {:else if platform === 'android'}
      <ol class="steps">
        <li>Tap the <strong>⋮</strong> menu button.</li>
        <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
      </ol>
    {:else if platform === 'desktop-firefox'}
      <p class="hint">
        Firefox doesn't currently support installing web apps. For the best experience,
        try Chrome, Edge, or Safari (macOS).
      </p>
    {:else}
      <p class="hint">
        Look in your browser's menu for "Install Lazy Traveler" or "Add to Home Screen".
        If you don't see it, try Chrome, Edge, or Safari.
      </p>
    {/if}
  </div>
</ModalShell>

<style>
  .content { display: flex; flex-direction: column; gap: 1rem; }
  .lede {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: var(--color-body);
  }
  .install-cta { align-self: flex-start; min-width: 140px; }
  .steps {
    margin: 0;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-size: 14px;
    line-height: 1.55;
    color: var(--color-body);
  }
  .steps li { padding-left: 0.25rem; }
  .steps strong { color: var(--color-ink); font-weight: 600; }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--color-muted);
    line-height: 1.5;
  }
</style>
